import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { movies } from '@/mocks/movies';
import { MovieCard } from '@/pages/home/components/MovieGrid';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.category === movie.category)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="w-full px-4 md:px-6 lg:px-10 pt-24 pb-20">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-500">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-200 font-heading mb-2">
              影片未找到
            </h2>
            <p className="text-foreground-500 mb-6">抱歉，您查找的影片不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.category, href: `/category/${getCategorySlug(movie.category)}` },
            { label: movie.title },
          ]}
        />

        {/* Hero Banner */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-10">
            {/* Poster */}
            <div className="flex-shrink-0 w-full sm:w-64 md:w-80 mx-auto lg:mx-0">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] bg-background-100">
                <img
                  src={movie.poster}
                  alt={movie.title}
                  title={`${movie.title} - 大师兄影视免费在线观看`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500 text-sm"></i>
                  <span className="text-white text-sm font-bold">{movie.rating}</span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-100 font-heading mb-3">
                {movie.title}
              </h1>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-300 border border-primary-500/20 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { label: '导演', value: movie.director },
                  { label: '主演', value: movie.actors.join(' / ') },
                  { label: '类型', value: movie.categories.slice(1).join(' / ') },
                  { label: '地区', value: movie.region },
                  { label: '时长', value: movie.duration },
                  { label: '年份', value: String(movie.year) },
                ].map((meta) => (
                  <div key={meta.label} className="bg-background-100 rounded-xl px-4 py-3 border border-background-200/40">
                    <p className="text-xs text-foreground-500 mb-1">{meta.label}</p>
                    <p className="text-sm text-foreground-200 font-medium truncate">{meta.value}</p>
                  </div>
                ))}
              </div>

              {/* Action button */}
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-base font-medium transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-xl"></i>
                立即播放
              </a>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="w-full px-4 md:px-6 lg:px-10 pb-8 md:pb-12">
          <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading mb-4">
            剧情简介
          </h2>
          <div className="bg-background-100 rounded-2xl p-6 border border-background-200/40">
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
              {movie.description}
            </p>
          </div>
        </section>

        {/* Related */}
        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-100 font-heading">
                相关推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}

        {/* SEO Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Movie',
              name: movie.title,
              description: movie.description,
              image: movie.poster,
              dateCreated: String(movie.year),
              director: {
                '@type': 'Person',
                name: movie.director,
              },
              actor: movie.actors.map((a) => ({
                '@type': 'Person',
                name: a,
              })),
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: movie.rating,
                bestRating: '10',
                ratingCount: Math.floor(Math.random() * 50000) + 10000,
              },
              genre: movie.categories.slice(1),
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: `${movie.title} - 大师兄影视`,
              description: `在线观看${movie.title}，大师兄影视提供${movie.title}高清全集播放，不卡顿秒开播放。${movie.description.substring(0, 100)}...`,
              url: `https://www.dashixiong.com/article/${movie.id}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '大师兄影视',
                url: 'https://www.dashixiong.com/',
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}

function getCategorySlug(categoryName: string): string {
  const map: Record<string, string> = {
    '电影': 'dianying',
    '电视剧': 'dianshiju',
    '动漫': 'dongman',
    '综艺': 'zongyi',
    '纪录片': 'jilupian',
  };
  return map[categoryName] || 'dianying';
}